import { Box, Typography, useTheme } from '@mui/material';
import { buildFriendlyConversationId } from '@provision/formatting-utils';
import { buildEditorExtensions, generateHTML, JSONContent } from '@provision/rich-text-editor';
import dayjs, { type Dayjs } from 'dayjs';
import type React from 'react';
import { DynamicIcon, Tooltip } from 'src/components';
import { EllipsesMenu, EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { MENTION_CLASS } from 'src/components/TextWithUI/mentions/const';
import { mentionChipStyles } from 'src/components/TextWithUI/mentions/use-text-with-ui-mentions';
import { ConversationMessageDraft, type ConversationStatusOption } from 'src/graphql';
import { SinceTimeCell } from 'src/pages/CustomViews/custom-views-entities-table';
import { htmlToPlainText, removeImageTagsFromHtml } from 'src/utils/text-parsing';
import { DraftClickHandler, TextContainer } from './draft-item.styles';

const STATIC_EXTENSIONS = buildEditorExtensions({
  mode: 'static',
  mentionClassName: MENTION_CLASS,
});

enum DraftStatus {
  None = 'none',
  Scheduled = 'scheduled',
  NotSent = 'not_sent',
}

type StatusMeta = {
  label: 'Scheduled' | 'Not sent' | '';
  icon: 'schedule' | 'do_not_disturb_on';
  color: string;
  tooltip?: string;
};

const getDraftStatus = (
  scheduledAt?: string | null,
  discardedAt?: string | null,
): DraftStatus => {
  if (!scheduledAt) return DraftStatus.None;
  return discardedAt ? DraftStatus.NotSent : DraftStatus.Scheduled;
};

const statusMetaMap: Record<
  DraftStatus,
  (p: { scheduledAt?: string | null; discardedAt?: string | null }) => StatusMeta
> = {
  [DraftStatus.None]: () => ({ label: '', icon: 'schedule', color: '' }),
  [DraftStatus.Scheduled]: ({ scheduledAt }) => ({
    label: 'Scheduled',
    icon: 'schedule',
    color: '#1976d2',
    tooltip: scheduledAt ? `Scheduled for ${formatScheduledFor(scheduledAt)}` : undefined,
  }),
  [DraftStatus.NotSent]: ({ scheduledAt }) => ({
    label: 'Not sent',
    icon: 'do_not_disturb_on',
    color: '#9e9e9e',
    tooltip: scheduledAt ? `Was scheduled for ${formatScheduledFor(scheduledAt)}` : undefined,
  }),
};

const badgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 0.5,
  px: 0.75,
  py: 0.25,
  borderRadius: 1,
  fontSize: '0.6875rem',
  lineHeight: 1.2,
  userSelect: 'none',
  whiteSpace: 'nowrap',
} as const;

type StatusBadgeProps = {
  status: DraftStatus;
  scheduledAt?: string | null;
  discardedAt?: string | null;
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, scheduledAt, discardedAt }) => {
  if (status === DraftStatus.None) return null;
  const meta = statusMetaMap[status]({ scheduledAt, discardedAt });

  return (
    <Tooltip title={meta.tooltip ?? ''}>
      <Box
        component="span"
        sx={{
          ...badgeSx,
          bgcolor: (t) => t.palette.action.hover,
          border: `1px solid ${meta.color}`,
        }}
        aria-label={meta.label}
      >
        <DynamicIcon name={meta.icon} weight={300} sx={{ fontSize: 16, color: meta.color }} />
        {meta.label}
      </Box>
    </Tooltip>
  );
};

const SuggestedBadge = ({ sourceLabel }: { sourceLabel?: string }) => {
  const label = sourceLabel ? `Suggested reply from ${sourceLabel}` : 'Suggested reply';

  return (
    <Tooltip title={label}>
      <Box
        component="span"
        aria-label={label}
        sx={{
          ...badgeSx,
          border: (t) => `1px solid ${t.palette.custom.OV.sys.tertiary}`,
          color: (t) => t.palette.custom.OV.sys.tertiary,
        }}
      >
        <DynamicIcon library="lucide" name="sparkles" size={14} />
        Suggested
      </Box>
    </Tooltip>
  );
};

const CardContainer: React.FC<
  React.PropsWithChildren<{ color?: string; isSelected?: boolean }>
> = ({ children, color, isSelected }) => (
  <Box
    sx={{
      position: 'relative',
      height: '100%',
      width: '100%',
      p: '1rem',
      borderRadius: 1,
      border: (t) => `1px solid ${isSelected ? t.palette.primary.main : t.palette.divider}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        borderTopLeftRadius: 4,
        borderBottomLeftRadius: 4,
        bgcolor: color || 'transparent',
      },
    }}
  >
    {children}
  </Box>
);

export interface DraftItemProps {
  draft: ConversationMessageDraft;
  isSelected?: boolean;
  conversationStatus?: Pick<
    ConversationStatusOption,
    'name' | 'icon' | 'iconLibrary' | 'color'
  >;
  onClick: (draft: ConversationMessageDraft) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  menuItems?: EllipsesMenuOption[];
}

export const DraftItem = ({
  draft,
  conversationStatus,
  isSelected = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  menuItems,
}: DraftItemProps) => {
  const theme = useTheme();
  if (!draft.conversation) return null;

  const subject = draft.conversation.subject ?? 'No Subject';
  const friendlyId = buildFriendlyConversationId(
    draft.partnerId,
    draft.conversation.workspaceConversationId,
  );
  const { previewHtml, previewText } = getDraftPreview(draft.message || '');
  const updatedAt = draft.updatedAt || draft.createdAt;

  const status = getDraftStatus(draft.scheduledAt, draft.discardedAt);
  const meta = statusMetaMap[status]({
    scheduledAt: draft.scheduledAt,
    discardedAt: draft.discardedAt,
  });

  const statusColor = status === DraftStatus.None ? undefined : meta.color;
  const accentColor = draft.userId ? statusColor : theme.palette.custom.OV.sys.tertiary;

  return (
    <DraftClickHandler
      onClick={() => onClick(draft)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      focusRipple
      component="div"
      isSelected={isSelected}
    >
      <CardContainer color={accentColor} isSelected={isSelected}>
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
          width="100%"
        >
          <Box>
            {/* header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="1rem">
              <TextContainer>
                <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                  {!!conversationStatus && (
                    <Tooltip title={conversationStatus.name}>
                      <DynamicIcon
                        weight={300}
                        library={conversationStatus.iconLibrary}
                        name={conversationStatus.icon}
                        sx={{ mr: '0.5rem', color: conversationStatus.color }}
                      />
                    </Tooltip>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ lineHeight: '1rem', fontWeight: 600, minWidth: 0 }}
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                    overflow="hidden"
                  >
                    {friendlyId}: {subject}
                  </Typography>
                </Box>
              </TextContainer>

              <Box display="flex" alignItems="center" gap={1}>
                {!draft.userId ? <SuggestedBadge sourceLabel={draft.source?.label} /> : null}
                <StatusBadge
                  status={status}
                  scheduledAt={draft.scheduledAt}
                  discardedAt={draft.discardedAt}
                />
                {!!menuItems?.length && <EllipsesMenu options={menuItems} />}
              </Box>
            </Box>

            {/* preview */}
            <Box sx={{ maxWidth: 400, minWidth: 0, ...mentionChipStyles(theme) }}>
              <Tooltip title={previewText}>
                <Box
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    fontSize: '0.775rem',
                  }}
                  dangerouslySetInnerHTML={{ __html: previewHtml }}
                />
              </Tooltip>
            </Box>
          </Box>

          {/* footer */}
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap="0.5rem" flex="0 0 auto">
              <DynamicIcon
                name={draft.isPrivate ? 'note_stack' : 'chat_bubble'}
                filled
                color={(t) =>
                  draft.isPrivate
                    ? t.palette.custom.OV.custom.InternalNotes.noteInputContainer
                    : t.palette.custom.OV.sys.secondary
                }
              />
              <Typography variant="body2">
                {draft.isPrivate ? 'Internal Note' : 'Public Reply'}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap="0.5rem" flex="0 0 auto">
              <DynamicIcon name="schedule" />
              <SinceTimeCell date={updatedAt} />
            </Box>
          </Box>
        </Box>
      </CardContainer>
    </DraftClickHandler>
  );
};

function isDoc(obj: unknown): obj is JSONContent {
  return (
    !!obj &&
    typeof obj === 'object' &&
    ('type' in obj || Array.isArray((obj as { content: unknown }).content))
  );
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getDraftPreview(
  message: string,
  limit = 60,
): { previewHtml: string; previewText: string } {
  if (!message) return { previewHtml: '', previewText: '' };
  try {
    const parsed = JSON.parse(message) as JSONContent;
    if (isDoc(parsed)) {
      const html = generateHTML(parsed, STATIC_EXTENSIONS);
      const htmlWithoutImages = removeImageTagsFromHtml(html);
      const text = htmlToPlainText(htmlWithoutImages).trim();
      return { previewHtml: htmlWithoutImages, previewText: text.slice(0, limit) };
    }
  } catch {
    /* fall back */
  }
  const text = htmlToPlainText(message).trim();
  return { previewHtml: escapeHtml(text.slice(0, limit)), previewText: text.slice(0, limit) };
}

function formatScheduledFor(scheduledAt?: string | null): string | null {
  if (!scheduledAt) return null;
  const d: Dayjs = dayjs(scheduledAt);
  if (!d.isValid()) return null;
  const fmt = d.year() === dayjs().year() ? 'MMM D [at] h:mm A' : 'MMM D, YYYY [at] h:mm A';
  return d.format(fmt);
}
